import { Box, Card, CardActionArea, Chip, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ChevronRight from '@mui/icons-material/ChevronRight';
import FlightOutlined from '@mui/icons-material/FlightOutlined';
import DirectionsBoatOutlined from '@mui/icons-material/DirectionsBoatOutlined';
import { StatusChip } from '../../components/StatusChip';
import { toneColor } from '../../components/tone';
import { useApprovalsFor } from '../approvals/useApprovals';
import { chaseOf, STAGE_TONE, type Mode, type Place, type Shipment } from './sample';

export const ModeIcon = ({ mode }: { mode: Mode }) =>
  mode === 'Air' ? <FlightOutlined fontSize="small" /> : <DirectionsBoatOutlined fontSize="small" />;

const placeText = (p: Place) => (p.code ? `${p.city} (${p.code})` : p.city);

/** "Frankfurt (FRA) → Dubai (DWC)"; just the city names when the vault has no airport or port codes */
export const RouteText = ({ s }: { s: Shipment }) => (
  <span>
    {placeText(s.from)} → {placeText(s.to)}
  </span>
);

/** One shipment as a card in the grid */
export function ShipmentCard({ s }: { s: Shipment }) {
  const approvals = useApprovalsFor()(s.srb);
  const chase = chaseOf(s);
  return (
    <Card>
      <CardActionArea component={RouterLink} to={`/shipments/${s.srb}`} sx={{ p: 1.75 }}>
        <Stack spacing={1.25}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography sx={{ fontWeight: 750, fontSize: 18 }}>{s.srb}</Typography>
            <Box sx={{ color: 'text.secondary', display: 'flex' }}>
              <ModeIcon mode={s.mode} />
            </Box>
            <StatusChip label={s.stage} tone={STAGE_TONE[s.stage]} />
            {chase && <StatusChip label={`Chase ${chase.who}`} tone="warning" />}
          </Stack>
          <Box>
            <Typography sx={{ fontWeight: 600 }}>{s.customer}</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              <RouteText s={s} />
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {s.service} · {s.cargo}
            <br />
            {s.load}
          </Typography>
          <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">
              ETD <b>{s.etd}</b>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ETA <b>{s.eta}</b>
            </Typography>
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: 'center', p: 1, borderRadius: 1.5, bgcolor: 'action.hover' }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Next Action
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {s.next.text}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 700, color: toneColor(s.next.tone), whiteSpace: 'nowrap' }}>
              {s.next.due}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Chip
              size="small"
              color={approvals ? 'error' : 'default'}
              variant={approvals ? 'filled' : 'outlined'}
              label={`${approvals} ${approvals === 1 ? 'Approval' : 'Approvals'}`}
            />
            <Chip
              size="small"
              color={s.messages ? 'primary' : 'default'}
              variant={s.messages ? 'filled' : 'outlined'}
              label={`${s.messages} New ${s.messages === 1 ? 'message' : 'messages'}`}
            />
            <Box sx={{ flex: 1 }} />
            <ChevronRight color="disabled" />
          </Stack>
        </Stack>
      </CardActionArea>
    </Card>
  );
}
